import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { getFullUrl } from '../../utils/utils';
import { show } from '@ebay/nice-modal-react';
import parse from 'html-react-parser';
import { formatBrandName, supportHostnameFor } from '../../utils/utils';

const SUPPORT_BASE_URL = 'https://support';
const SHIPPLAY_PARTNER_URL = 'https://support.shipplay.com/hc/en-us/articles/18737832414996-Becoming-a-partner';
const SHIPSTICKS_PARTNER_URL = 'https://shipsticks.com/partnership-benefits';

const Footer = ({
  bgColor = '#1C2938', FooterLogo, header, body, children, brand, shipLinkPath, subdomain,
  supportPhoneNumber = '(855) 792-7529', logoSize = 'w-[14rem] xl:w-[261px]',
}) => {
  function getSupportLink(path) {
    const isProductionEnv = window.production_env === 'true';
    if (!isProductionEnv) return getFullUrl(path);

    if (path === '/partners') {
      return brand === 'shipplay' ? SHIPPLAY_PARTNER_URL : SHIPSTICKS_PARTNER_URL;
    }

    return `${SUPPORT_BASE_URL}.${brand}.com`;
  }

  const brandName = formatBrandName(brand);
  const supportHostname = supportHostnameFor(brand);
  const year = useMemo(() => new Date().getFullYear(), [])

  return (
    <footer
      className="text-white px-6 py-8 sm:p-8 lg:px-14 xl:px-36 2xl:px-56 font-poppins"
      style={{ backgroundColor: bgColor }}
    >
      <div className="max-w-7xl mx-auto">
        {header && body && (
          <div className="text-left pb-8 lg:py-8 pl-[5%] lg:pl-0 gap-12">
            <h5 className="text-white text-xl font-semibold mb-2">{header}</h5>
            <p className="mb-2 text-lg leading-8">{parse(body)}</p>
          </div>
        )}
        {children}
        {/* Main Footer Links */}
        <div className={`
          flex flex-col lg:flex-row flex-wrap justify-center gap-8 lg:justify-between
          py-8 ${header && body ? 'border-t border-[#FFF] border-opacity-20' : ''} w-full
        `}>
          {/* First Section */}
          <div className="flex flex-row lg:flex-col justify-center lg:justify-around gap-8">
            {/* Column: Brand Name */}
            <div className="flex flex-col flex-1 pl-[5%] lg:pl-0">
              <h5 className="text-white text-[22px] sm:text-2xl lg:text-lg xl:text-[22px] font-semibold mb-4">
                {brandName}
              </h5>
              <a href={getFullUrl('/', subdomain)} className="hover:text-white mb-2">Home</a>
              <a href={getFullUrl(shipLinkPath, subdomain)} className="hover:text-white mb-2">Ship</a>
              <a href={getFullUrl('/track', subdomain)} className="hover:text-white mb-2">Track</a>
              <a href={getFullUrl('/gift-cards', subdomain)} className="hover:text-white mb-2">Gift Cards</a>
            </div>
            {/* Column: Help */}
            <div className="flex flex-col flex-1 pl-[15%] lg:pl-0">
              <h5 className="text-white text-[22px] sm:text-2xl lg:text-lg xl:text-[22px] font-semibold mb-4">Help</h5>
              <a href={getFullUrl('/how-it-works', subdomain)} className="hover:text-white mb-2">How it works</a>
              <a href={getSupportLink('/support')} target="_blank" rel="noreferrer" className="hover:text-white mb-2">
                Support
              </a>
              <a href={getFullUrl('/claims', subdomain)} className="hover:text-white mb-2">Claims</a>
              <a href={getFullUrl('/store_locator', subdomain)} className="hover:text-white mb-2">Drop-off locator</a>
            </div>
          </div>

          {/* Second Section */}
          <div className="flex flex-row lg:flex-col flex-wrap justify-around gap-8 lg:justify-start lg:p-0">
            {/* Column: Partner with us */}
            <div className="flex flex-col flex-1 pl-[5%] lg:pl-0">
              <h5 className="text-white text-[22px] sm:text-2xl lg:text-lg xl:text-[22px] font-semibold mb-4">
                Partner with us
              </h5>
              <a href={getSupportLink('/partners')} target="_blank" rel="noreferrer" className="hover:text-white mb-2">
                Become a partner
              </a>
            </div>
            {/* Column: About us */}
            <div className="flex flex-col flex-1 pl-[15%] lg:pl-0">
              <h5 className="text-white text-[22px] sm:text-2xl lg:text-lg xl:text-[22px] font-semibold mb-4">
                About us
              </h5>
              <a href={getFullUrl('/about', subdomain)} className="hover:text-white mb-2">About Us</a>
              <a href={getFullUrl('/contact', subdomain)} className="hover:text-white mb-2">Contact Us</a>
            </div>
          </div>

          {/* Third Section */}
          <div className="flex flex-col flex-wrap lg:items-right gap-9">
            {/* Column: Get in touch */}
            <div className="flex flex-col lg:text-right lg:p-0 pl-[5%] lg:pl-0">
              <h5 className="text-white text-[22px] sm:text-2xl lg:text-lg xl:text-[22px] font-semibold mb-4">
                Get in touch
              </h5>
              <p className="mb-2">11400 N. Jog Rd, Suite 300<br />Palm Beach Gardens<br /> Florida 33418</p>
              <a className="mb-2" href={`tel:${supportPhoneNumber.replace(/\D/g, '')}`}>{supportPhoneNumber}</a>
              <a className="hover:text-white mb-2" href={`mailto:info@${supportHostname}`}>
                {`info@${supportHostname}`}
              </a>
            </div>
            {/* Column: Logo */}
            <div className="flex justify-center lg:justify-end items-center lg:pt-8">
              <a href={getFullUrl('/', subdomain)} >
                <img
                  src={FooterLogo}
                  alt="Footer Logo"
                  className={`${logoSize}`}
                />
              </a>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="
          flex flex-col lg:flex-row justify-between gap-5 items-center border-t border-[#FFF] border-opacity-20 pt-8
        ">
          <div className="
            text-xs sm:text-sm lg:text-xs xl:text-sm hover:text-white flex flex-col lg:flex-row gap-4 lg:gap-0
          ">
            <div className="flex justify-center flex-nowrap">
              <a href={getFullUrl('/terms', subdomain)} className="mr-2">Terms of Use</a>|
              <a href={getFullUrl('/privacy', subdomain)} className="mx-2">Privacy Policy</a>
            </div>
            <div className="hidden lg:block">|</div>
            <div className="flex justify-center flex-nowrap ">
              <a href={getFullUrl('/cookie-policy', subdomain)} className="mx-2">Cookie Policy</a>|
              <a href={getFullUrl('/usage-agreement', subdomain)} className="mx-2">Usage Right</a>|
              <a href={getSupportLink('/support')} target="_blank" rel="noreferrer" className="mx-2">Support</a>|
              <a href={getFullUrl('/sitemap', subdomain)} className="ml-2">Site Map</a>
            </div>
          </div>
          {/* Copyright */}
          <div>
            <p className="text-xs sm:text-sm lg:text-xs 2xl:text-sm">
              &copy; {`${year} ${brandName}. All Rights Reserved.`}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  header: PropTypes.string,
  body: PropTypes.string,
  bgColor: PropTypes.string,
  FooterLogo: PropTypes.string.isRequired,
  brand: PropTypes.string,
  children: PropTypes.node,
  shipLinkPath: PropTypes.string.isRequired,
  subdomain: PropTypes.string,
  supportPhoneNumber: PropTypes.string,
  logoSize: PropTypes.string,
};

export default Footer;
